import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { t } from '@reos/i18n';
import { PropertyImage } from './PropertyImage.js';
import { ParcelMap } from './ParcelMap.js';
import { caseHeroImage, type CaseHeroImage } from '../lib/case-maps-api.js';
import { fetchCaseGeometry } from '../lib/parcel-map-api.js';
import { NAHLIZENI_DO_KN_PARCEL_URL } from '../lib/parcel-panel-rows.js';
import { fieldLabel, formatValue } from './field-intake/values.js';
import {
  casePropertyFactRows,
  originCaption,
  originLegend,
  ORIGIN_TAG_CLASS,
  propertyFactRows,
  type PropertyFactRow,
} from '../lib/case-origin.js';
import type { CaseDetail } from '../lib/dd-api.js';
import type { CaseParticipant } from '../lib/cases-api.js';
import {
  formatMoney,
  type PropertyFact as PropertyCardFact,
  type PropertyFactConflict,
} from '../lib/properties.js';

/**
 * Krok UXR2 — PRAVY SLOUPEC KOKPITU podle predlohy `03-detail-zakazky.html`:
 * ortofoto, karta „Nemovitost" se stitky puvodu a karta „Lidé".
 *
 * Nic se tu nepocita: hodnoty jdou z `lib/case-origin.ts` (ten jen cte
 * existujici selektory `@reos/reports`), nahled z hotoveho vyrezu pripadu
 * (`lib/case-maps-api.ts`) nebo ze stejne kaskady jako karty zakazek
 * (`PropertyImage`).
 */

const cs = { locale: 'cs' } as const;

export interface CaseThumbFallback {
  readonly coverUrl: string | null;
  readonly lat: number | null;
  readonly lon: number | null;
  /** Krok UXF1 bod 2: nemovitost, ke ktere server dopocita obrazek z Mapy.com. */
  readonly propertyId: string | null;
}

/**
 * Hlavni nahled zakazky.
 *
 * Krok UXR5 bod 9 — PORADI (predloha `Zakazka.dc.html`: „ortofoto s hranicemi
 * 3 parcel"):
 *  1. hotovy VYREZ K2 ze serveru — tentyz obrazek, jaky jde do reportu,
 *  2. ZIVA MAPA M1 s hranicemi parcel teto zakazky (`ParcelMap compact`) —
 *     ortofoto + katastr + obrys parcel, tedy tyz renderer, jaky kresli
 *     mapovou kartu v sekci „Geometrie",
 *  3. obrazek nemovitosti (`PropertyImage` — fotka → letecky vyrez
 *     s hranici parcel → panorama; krok UXV4a bod 4, stred obce uz v kaskade
 *     NENI: predloha UX v4 § Pravidla 3 „Nikdy střed obce"),
 *  4. VETA „Mapa po doplnění adresy" — ne seda plocha.
 *
 * Do UXR5 se po kroku 1 skakalo rovnou na 3, takze zakazka bez hotoveho
 * vyrezu (na preview VSECHNY demo zakazky) mela vpravo prazdny podklad s
 * teckou adresy — nalez R.B. 3. 10. 08:41.
 */
export function CaseHero({
  ddCaseId,
  fallback,
  inset = false,
}: {
  readonly ddCaseId: string;
  readonly fallback: CaseThumbFallback;
  /**
   * Krok UXV4a2: `true` = nahled sedi UVNITR foto karty v4 (`V4PhotoCard`),
   * ktera ma vlastni ramecek s nesymetrickym orezem. Licencni poznamka pak
   * jde do `title` obrazku — stejne, jako ji nese letecky vyrez vsude jinde
   * v UX v4 (`CaseOrtho`); popisek pod fotkou by orez rozbil.
   */
  readonly inset?: boolean;
}): React.JSX.Element {
  const [hero, setHero] = React.useState<CaseHeroImage | null>(null);
  /** `null` = jeste nevime, `true/false` = ma zakazka geometrii parcel. */
  const [hasGeometry, setHasGeometry] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    let alive = true;
    setHero(null);
    setHasGeometry(null);
    // Chybejici vyrez NENI chyba — je to stav, ktery ma nahled pojmenovat
    // tim, ze spadne na zivou mapu nebo fotku (viz kaskada vyse).
    void caseHeroImage(ddCaseId).then(
      (result) => { if (alive) setHero(result); },
      () => undefined,
    );
    // Geometrii cteme TADY, ne az v mape: bez ni by se mapa vykreslila nad
    // stredem republiky a predstirala by, ze nekam miri.
    void fetchCaseGeometry(ddCaseId).then(
      (geometry) => { if (alive) setHasGeometry(geometry.features.length > 0); },
      () => { if (alive) setHasGeometry(false); },
    );
    return () => { alive = false; };
  }, [ddCaseId]);

  if (hero !== null) {
    const image = (
      <img
        className={inset ? 'rh-v4-photo__src' : 'rh-a2-hero'}
        src={hero.url}
        alt={t('webAdmin.caseDetail.propertyCard.heroAlt', cs)}
        {...(hero.licenseNote !== null ? { title: hero.licenseNote } : {})}
        data-testid={inset ? 'case-hero-orthophoto' : undefined}
      />
    );
    if (inset) return image;
    return (
      <figure style={{ margin: 0 }} data-testid="case-hero-orthophoto">
        {image}
        <figcaption className="rh-a2-meta" style={{ marginTop: 4 }}>
          {hero.licenseNote}
        </figcaption>
      </figure>
    );
  }
  if (hasGeometry === null) {
    /*
     * Dokud nevime, jestli zakazka ma geometrii parcel, nahled NIC
     * nepredstira: fotka ani mapa adresy se neukazou, aby po doplavani
     * odpovedi neproblikly do mapy parcel (nalez pri zivem overeni UXR5 —
     * na mobilu se stihl vykreslit prave ten mezistav).
     */
    return <span className="rh-a2-hero rh-a2-hero--box" data-testid="case-hero-loading" aria-hidden="true" />;
  }
  if (hasGeometry === true) {
    return (
      <div
        className={inset ? 'rh-v4-photo__src' : 'rh-a2-hero rh-a2-hero--map'}
        data-testid="case-hero-parcel-map"
      >
        <ParcelMap caseId={ddCaseId} compact />
      </div>
    );
  }
  if (fallback.coverUrl === null && fallback.lat === null) {
    // Zadna souradnice ani fotka: veta misto sede plochy (zadani bod 9).
    return (
      <p
        className={inset ? 'rh-v4-photo__none' : 'rh-a2-hero rh-a2-hero--none'}
        data-testid="case-hero-no-map"
      >
        {t('webAdmin.caseDetail.propertyCard.mapAfterAddress', cs)}
      </p>
    );
  }
  return (
    <PropertyImage
      photoUrl={fallback.coverUrl}
      propertyId={fallback.propertyId}
      lat={fallback.lat}
      lon={fallback.lon}
      {...(inset ? {} : { className: 'rh-a2-hero' })}
      testIdPrefix="case-hero"
    />
  );
}

function OriginTag({ row }: { readonly row: PropertyFactRow }): React.JSX.Element {
  return (
    <span className={`rh-a2-tag ${ORIGIN_TAG_CLASS[row.origin]}`} data-origin={row.origin}>
      {originCaption(row.origin)}
    </span>
  );
}

/**
 * Krok APP3 bod 5 (nalez A14, R78 bod 3) — KTERY UDAJ PLATI.
 *
 * Kdyz se uredni udaj lisi od toho, co clovek potvrdil, projekce ho
 * NEPREPISE (spravne) — ale do APP3 o tom karta MLCELA a uredni hodnota se
 * zahodila. Rozpor ted stoji nad udaji karty, jmenuje OBE hodnoty i zdroj
 * uradu a nabizi dve tlacitka. Rozhodnuti jde na server s auditni stopou
 * a prezije dalsi prověření.
 */
function FactConflicts({
  conflicts,
  onResolve,
}: {
  readonly conflicts: readonly PropertyFactConflict[];
  readonly onResolve?: ((factKey: string, choice: 'manual' | 'registry') => void) | undefined;
}): React.JSX.Element | null {
  if (conflicts.length === 0) return null;
  return (
    <div className="rh-a2-conflict" role="alert" data-testid="case-fact-conflicts">
      <p className="rh-a2-conflict__head">
        {t('webAdmin.caseDetail.propertyFacts.conflictHeading', cs)}
      </p>
      <ul>
        {conflicts.map((conflict) => {
          const registry = formatConflictValue(conflict.factKey, conflict.registryValue);
          const verified = formatConflictValue(conflict.factKey, conflict.verifiedValue);
          return (
            <li key={conflict.factKey} data-testid={`case-fact-conflict-${conflict.factKey}`}>
              <span>
                {verified === null
                  ? t('webAdmin.caseDetail.propertyFacts.conflictRowNoVerified', {
                      ...cs,
                      values: { label: fieldLabel(conflict.factKey), registry: registry ?? '' },
                    })
                  : t('webAdmin.caseDetail.propertyFacts.conflictRow', {
                      ...cs,
                      values: {
                        label: fieldLabel(conflict.factKey),
                        registry: registry ?? '',
                        verified,
                      },
                    })}
              </span>{' '}
              <span className="rh-a2-conflict__src">
                {t('webAdmin.caseDetail.propertyFacts.conflictSource', {
                  ...cs,
                  values: {
                    source: conflict.registrySourceRef ?? conflict.registrySourceType,
                    at: conflict.registryCapturedAt.slice(0, 10),
                  },
                })}
              </span>
              {onResolve !== undefined && (
                <span className="rh-a2-conflict__actions">
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => onResolve(conflict.factKey, 'manual')}
                    data-testid={`case-fact-conflict-keep-${conflict.factKey}`}
                  >
                    {t('webAdmin.caseDetail.propertyFacts.conflictKeepManual', cs)}
                  </button>
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => onResolve(conflict.factKey, 'registry')}
                    data-testid={`case-fact-conflict-take-${conflict.factKey}`}
                  >
                    {t('webAdmin.caseDetail.propertyFacts.conflictTakeRegistry', cs)}
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="rh-a2-conflict__note" data-testid="case-fact-conflict-note">
        {t('webAdmin.caseDetail.propertyFacts.conflictNote', cs)}
      </p>
    </div>
  );
}

/**
 * Hodnota rozporu do vety.
 *
 * Krok APP3 (pokracovani): formatuje ji `formatValue` — TENTYZ prevod, jakym
 * jsou vysazene radky mrizky karty i nabor (`field-intake/values.ts`). Dokud
 * tu stal `JSON.stringify`, cetla makléřka u parcel `[{"number":"38/13",…}]`:
 * druhy (slabsi) zapis teze hodnoty vedle jednoho spravneho.
 *
 * `null` = hodnota, o ktere se nema co rikat (prazdna, nezname).
 */
function formatConflictValue(factKey: string, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const text = formatValue(factKey, value);
  return text.trim() === '' ? null : text;
}

/**
 * Krok P8F6 bod 3 — BLOK „ÚDAJE NEMOVITOSTI" Z KARTY (nalez P8-N11).
 *
 * Po naboru detail zakazky ukazoval jen parcely a vymeru, prestoze cena,
 * plocha, dispozice, patro i jednotka uz na karte nemovitosti JSOU. Kresli
 * se TYMZ zpusobem jako karta „Nemovitost" vyse (radek + stitek puvodu),
 * jen zdroj je jiny: `property_facts` (platne pro zakazku s proverením i
 * bez nej), ne auditni pripad. Prazdny blok se nekresli.
 */
export function CasePropertyFactsCard({
  facts,
  conflicts = [],
  onResolveConflict,
}: {
  readonly facts: readonly PropertyCardFact[];
  /**
   * Krok APP3 bod 5 (nalez A14, R78 bod 3): rozpory uredniho a potvrzeneho
   * udaje, o kterych makléřka rozhoduje. Prazdne = zadny rozpor.
   */
  readonly conflicts?: readonly PropertyFactConflict[] | undefined;
  readonly onResolveConflict?:
    | ((factKey: string, choice: 'manual' | 'registry') => void)
    | undefined;
}): React.JSX.Element | null {
  const rows = casePropertyFactRows(facts);
  if (rows.length === 0 && conflicts.length === 0) return null;
  return (
    <section className="rh-a2-card" data-testid="case-property-facts-card">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseDetail.propertyFacts.heading', cs)}</h2>
      </div>
      <FactConflicts conflicts={conflicts} onResolve={onResolveConflict} />
      <ul className="rh-a2-facts">
        {rows.map((row) => (
          <li key={row.key} data-testid={`case-property-fact-${row.key}`}>
            <span className="rh-a2-k">{row.label}</span>
            <span className="rh-a2-v">
              {row.value ?? ''} <OriginTag row={row} />
            </span>
          </li>
        ))}
      </ul>
      <p className="rh-a2-card__foot rh-a2-legend">
        {t('webAdmin.caseOrigin.legendLabel', cs)}
        {originLegend(rows).map((origin) => (
          <span key={origin} className={`rh-a2-tag ${ORIGIN_TAG_CLASS[origin]}`}>
            {originCaption(origin)}
          </span>
        ))}
      </p>
    </section>
  );
}

/** Karta „Nemovitost" — kazdy udaj se stitkem, odkud je (predloha `.facts`). */
export function CasePropertyCard({
  detail,
  onOpenAudit,
}: {
  readonly detail: CaseDetail;
  /** „Audit" neni zalozka (zadani UXR2) — je to tenhle odkaz. */
  readonly onOpenAudit: () => void;
}): React.JSX.Element {
  const rows = propertyFactRows(detail);
  return (
    <section className="rh-a2-card" data-testid="case-property-card">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseDetail.propertyCard.heading', cs)}</h2>
      </div>
      <ul className="rh-a2-facts">
        {rows.map((row) => (
          <li key={row.key} data-testid={`case-property-row-${row.key}`}>
            <span className="rh-a2-k">{row.label}</span>
            <span className="rh-a2-v">
              {row.value ?? ''} <OriginTag row={row} />
              {/*
                Zivy pruchod 13. 10. (V11): „Zobrazit v katastru" bylo jen
                v panelu parcely (ParcelPanel, krok KN4) — v karte
                „Nemovitost" zakazky, kde se radek s parcelou taky zobrazuje,
                takove tlacitko chybelo.
              */}
              {row.key === 'parcels' && row.value !== null && (
                <a
                  href={NAHLIZENI_DO_KN_PARCEL_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="rh-a2-link"
                  style={{ marginLeft: 8 }}
                  data-testid="case-property-cadastre-link"
                >
                  {t('webAdmin.parcelMap.chips.cadastre', cs)}
                </a>
              )}
            </span>
          </li>
        ))}
      </ul>
      <p className="rh-a2-card__foot rh-a2-legend">
        {t('webAdmin.caseOrigin.legendLabel', cs)}
        {originLegend(rows).map((origin) => (
          <span key={origin} className={`rh-a2-tag ${ORIGIN_TAG_CLASS[origin]}`}>
            {originCaption(origin)}
          </span>
        ))}
        <button type="button" className="rh-a2-link" onClick={onOpenAudit} data-testid="case-audit-details">
          {t('webAdmin.caseDetail.v2.auditDetails', cs)}
        </button>
      </p>
    </section>
  );
}

/** Popisky roli ucastniku — tentyz slovnik, jaky pouzivaji smluvni strany. */
const ROLE_CAPTION: Readonly<Record<string, string>> = {
  seller: t('webAdmin.contracts.editor.seller', cs),
  buyer: t('webAdmin.contracts.editor.buyer', cs),
  landlord: t('webAdmin.contracts.editor.landlord', cs),
  tenant: t('webAdmin.contracts.editor.tenant', cs),
};

/**
 * Role ucastnika zakazky lidsky (krok UXF2 bod 4). Sloupec „Role" v adresari
 * klientu cte TENTYZ slovnik jako karta „Lidé" v detailu zakazky — dve kopie
 * by se rozesly uz pri pate roli. Neznamou roli vraci tak, jak prisla
 * z API (radek nikdy nezmizi kvuli neznamemu kodu).
 */
export function caseRoleCaption(role: string): string {
  return ROLE_CAPTION[role] ?? role;
}

export interface CasePerson {
  readonly partyId: string;
  readonly role: string;
  readonly displayName: string;
}

export function casePersonsOf(
  participants: readonly CaseParticipant[],
  nameByParty: ReadonlyMap<string, string>,
): readonly CasePerson[] {
  return participants.map((item) => ({
    partyId: item.partyId,
    role: item.role,
    // Jmeno nenactene (403 na `party` nebo chyba site) — radek se NEZAHODI,
    // role sama uz je informace („je tu kupujici").
    displayName: nameByParty.get(item.partyId) ?? '',
  }));
}

/** Karta „Lidé" — makler zakazky a strany obchodu (predloha). */
export function CasePeopleCard({
  brokerName,
  openedAt,
  persons,
}: {
  readonly brokerName: string | null;
  readonly openedAt: string;
  readonly persons: readonly CasePerson[];
}): React.JSX.Element {
  return (
    <section className="rh-a2-card" data-testid="case-people-card">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseDetail.propertyCard.peopleHeading', cs)}</h2>
      </div>
      <ul className="rh-a2-facts">
        {persons.map((person) => (
          <li key={`${person.partyId}-${person.role}`}>
            <span className="rh-a2-k">{caseRoleCaption(person.role)}</span>
            <span className="rh-a2-v">
              {person.displayName === '' ? (
                <span className="rh-a2-muted">{t('webAdmin.klienti.loadingShort', cs)}</span>
              ) : (
                <Link to="/klienti" search={{ klient: person.partyId }}>
                  {person.displayName}
                </Link>
              )}
            </span>
          </li>
        ))}
        <li>
          <span className="rh-a2-k">{t('webAdmin.caseDetail.propertyCard.brokerLabel', cs)}</span>
          <span className="rh-a2-v">
            {brokerName ?? (
              <span className="rh-a2-muted">{t('webAdmin.caseDetail.v2.subtitleBrokerUnknown', cs)}</span>
            )}
          </span>
        </li>
        <li>
          <span className="rh-a2-k">{t('webAdmin.caseDetail.propertyCard.openedLabel', cs)}</span>
          <span className="rh-a2-v rh-a2-num">{new Date(openedAt).toLocaleDateString('cs-CZ', { dateStyle: 'medium' })}</span>
        </li>
      </ul>
    </section>
  );
}

/**
 * Krok UXR3 — KARTA „Výměry" se SOUCTEM (predloha `Zakazka.dc.html`).
 *
 * Parcely a jejich vymery jdou ze SDILENEHO resolveru `areaRows`
 * (`@reos/reports`) — tedy ze tehoz zdroje, z jakeho je bere PDF reportu.
 * Soucet se scita tady, protoze predloha ho ma; `areaReconciliationResult`
 * (zalozka Nemovitost) pocita NECO JINEHO — potvrzenou vymeru PREDMETU
 * PRODEJE, ktera bez uzavrene oblasti „Co se prodává" neexistuje. Dve
 * ruzna cisla ze dvou ruznych duvodu, proto ne jedna funkce.
 */
export function CaseAreasCard({
  rows,
}: {
  readonly rows: readonly { readonly parcelLabel: string; readonly areaM2: number }[];
}): React.JSX.Element | null {
  if (rows.length === 0) return null;
  const total = rows.reduce((sum, row) => sum + row.areaM2, 0);
  const m2 = (value: number): string =>
    `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(value)} m²`;
  return (
    <section className="rh-a2-card" data-testid="case-areas-card">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseDetail.areasHeading', cs)}</h2>
      </div>
      <ul className="rh-a2-facts rh-a2-facts--sum">
        {rows.map((row) => (
          <li key={row.parcelLabel}>
            <span className="rh-a2-k">{row.parcelLabel}</span>
            <span className="rh-a2-v">{m2(row.areaM2)}</span>
          </li>
        ))}
        <li className="rh-a2-facts__total" data-testid="case-areas-total">
          <span className="rh-a2-k">{t('webAdmin.caseDetail.areasTotal', cs)}</span>
          <span className="rh-a2-v">{m2(total)}</span>
        </li>
      </ul>
    </section>
  );
}

/**
 * Krok UXR3 — KARTA „Peníze" (predloha `Zakazka.dc.html`): provize a naklady
 * zakazky. Jedno volani `GET /v1/cases/:id/costs` (krok O6a) da OBOJI —
 * `commission` i soucty nakladu — takze karta nepridava druhy dotaz.
 */
export function CaseMoneyCard({
  commission,
  costTotals,
  loading,
  onOpenMoney,
}: {
  readonly commission: { readonly amount: string; readonly currency: string } | null;
  readonly costTotals: readonly { readonly currency: string; readonly total: string }[];
  readonly loading: boolean;
  readonly onOpenMoney: () => void;
}): React.JSX.Element {
  const costs = costTotals.filter((row) => Number(row.total) !== 0);
  return (
    <section className="rh-a2-card" data-testid="case-money-card">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseTabGroups.penize.label', cs)}</h2>
      </div>
      <ul className="rh-a2-facts rh-a2-facts--sum">
        <li data-testid="case-money-commission">
          <span className="rh-a2-k">{t('webAdmin.caseDetail.money.commission', cs)}</span>
          <span className="rh-a2-v">
            {loading
              ? '…'
              : (formatMoney(commission) ?? (
                  <span className="rh-a2-muted">{t('webAdmin.caseDetail.money.noCommission', cs)}</span>
                ))}
          </span>
        </li>
        <li data-testid="case-money-costs">
          <span className="rh-a2-k">{t('webAdmin.caseDetail.money.costs', cs)}</span>
          <span className="rh-a2-v">
            {loading ? (
              '…'
            ) : costs.length === 0 ? (
              <span className="rh-a2-muted">{t('webAdmin.caseDetail.money.noCosts', cs)}</span>
            ) : (
              costs
                .map((row) => formatMoney({ amount: row.total, currency: row.currency }))
                .filter((text): text is string => text !== null)
                .join(' · ')
            )}
          </span>
        </li>
      </ul>
      <p className="rh-a2-card__foot">
        <button type="button" className="rh-a2-link" onClick={onOpenMoney} data-testid="case-money-open">
          {t('webAdmin.caseDetail.money.open', cs)}
        </button>
      </p>
    </section>
  );
}
